import dayjs from 'dayjs';
import { createJSONStorage } from 'zustand/middleware';
import { createShallowStore, createStoreWithPersistAndSubscribe } from '@frontend/store';

export interface ComponentEngagementData {
  visibilityCount: number;
  interactionCount: number;
  daysSeenCount: number;
  lastSeen: string | null; // ISO date string
  firstSeen: string | null; // ISO date string
}

interface ComponentTrackingConfig {
  componentId: string;
  source?: string;
}

export interface ComponentEngagementStore {
  components: Record<string, ComponentEngagementData>;

  // Core methods
  trackVisibility: (config: ComponentTrackingConfig) => void;
  trackInteraction: (config: ComponentTrackingConfig) => void;
  getEngagementData: (config: ComponentTrackingConfig) => ComponentEngagementData;

  // Helper methods
  getDaysSinceLastSeen: (config: ComponentTrackingConfig) => number | null;
  resetEngagementData: (config: ComponentTrackingConfig) => void;
}

const getComponentUniqueId = (config: ComponentTrackingConfig): string => {
  const { componentId, source } = config;
  return [componentId, source].filter(Boolean).join(':');
};

const DEFAULT_ENGAGEMENT_DATA: ComponentEngagementData = {
  visibilityCount: 0,
  interactionCount: 0,
  daysSeenCount: 0,
  lastSeen: null,
  firstSeen: null,
};

export const useComponentEngagementStore = createStoreWithPersistAndSubscribe<ComponentEngagementStore>(
  (set, get) => ({
    components: {},

    trackVisibility: ({ componentId, source }: ComponentTrackingConfig) => {
      const today = new Date().toISOString().split('T')[0];
      const id = getComponentUniqueId({ componentId, source });
      const component = get().components[id] || { ...DEFAULT_ENGAGEMENT_DATA };

      set((state) => ({
        components: {
          ...state.components,
          [id]: {
            visibilityCount: component.visibilityCount + 1,
            interactionCount: component.interactionCount,
            daysSeenCount: component.lastSeen !== today ? component.daysSeenCount + 1 : component.daysSeenCount,
            lastSeen: today,
            firstSeen: component.firstSeen || today,
          },
        },
      }));
    },

    trackInteraction: ({ componentId, source }: ComponentTrackingConfig) => {
      const today = new Date().toISOString().split('T')[0];
      const id = getComponentUniqueId({ componentId, source });
      const component = get().components[id] || { ...DEFAULT_ENGAGEMENT_DATA };

      set((state) => ({
        components: {
          ...state.components,
          [id]: {
            visibilityCount: component.visibilityCount,
            interactionCount: component.interactionCount + 1,
            daysSeenCount: component.lastSeen !== today ? component.daysSeenCount + 1 : component.daysSeenCount,
            lastSeen: today,
            firstSeen: component.firstSeen || today,
          },
        },
      }));
    },

    getEngagementData: ({ componentId, source }: ComponentTrackingConfig) => {
      const id = getComponentUniqueId({ componentId, source });
      return get().components[id] ?? DEFAULT_ENGAGEMENT_DATA;
    },

    getDaysSinceLastSeen: ({ componentId, source }: ComponentTrackingConfig) => {
      const id = getComponentUniqueId({ componentId, source });
      const component = get().components[id];
      if (!component?.lastSeen) return null;

      return dayjs().diff(dayjs(component.lastSeen), 'day');
    },

    resetEngagementData: ({ componentId, source }: ComponentTrackingConfig) => {
      const id = getComponentUniqueId({ componentId, source });
      set((state) => ({
        components: {
          ...state.components,
          [id]: { ...DEFAULT_ENGAGEMENT_DATA },
        },
      }));
    },
  }),
  {
    name: 'component-engagement-store',
    storage: createJSONStorage(() => localStorage),
  },
  { name: 'component-engagement-store', trace: true }
);

export const useComponentEngagementShallowStore =
  createShallowStore<ComponentEngagementStore>(useComponentEngagementStore);
